import React from 'react';

const AuthorizationAgreement = ({ authData, onChange }) => {
  const agreementText = `🏛️ HOA MANAGER PRO

Association Authorization Agreement
(Board Officer or Authorized Representative)

Effective Date: Upon electronic acceptance
Governing Law and Venue: Santa Rosa County, Florida

⸻

1. Parties

This Agreement ("Agreement") is entered into between UnifiedOps LLC, d/b/a HOA Manager Pro ("Provider") and the homeowners' association, condominium association, or similar community organization identified below ("Association").

⸻

2. Authority to Bind

The individual accepting this Agreement ("Signatory") affirms that they are a Board Officer or Authorized Representative of the Association and possess full legal authority to execute this Agreement and bind the Association to the terms herein.

⸻

3. Scope and Relationship

The Association hereby engages HOA Manager Pro as a hosted software platform for community administration, including communication, voting, records management, and related services ("Services").
This Agreement does not create an agency, partnership, or joint venture between Provider and Association.

⸻

4. Acceptance of Terms and Privacy Policy

The Association acknowledges and accepts the Terms of Service and Privacy Policy published at www.hoamanagerpro.us/terms and www.hoamanagerpro.us/privacy, which are incorporated by reference.

⸻

5. Records and Custodianship

The Association acknowledges that HOA Manager Pro will serve as a designated electronic records custodian for digital documents, notices, minutes, and voting materials as adopted by the Board under applicable resolutions and Florida Statute §720.303 and §720.317.

The Association remains the owner and controller of its data; HOA Manager Pro acts solely as a data processor and custodian.

⸻

6. Governance and Fiduciary Disclaimer

The Association acknowledges that HOA Manager Pro is a software provider only and does not act as a property manager, community association manager (CAM), or fiduciary.
All decisions regarding voting, enforcement, communications, and record production are made by the Association and its duly elected Board of Directors.
The Provider shall not be held liable for the Association's governance actions, omissions, or compliance with state or federal law.

DIY Platform & Legal Responsibility Acknowledgment:
The Association acknowledges that HOA Manager Pro is a flexible, do-it-yourself software platform and may not always reflect the most current statutory changes, legal requirements, or industry standards. The Association remains solely responsible for ensuring compliance with all applicable laws, governing documents, voting requirements, notice procedures, and record rules, and for consulting legal counsel as needed.

⸻

7. Data Handling and Security

HOA Manager Pro implements reasonable administrative, technical, and physical safeguards to protect stored data.
The Association agrees to use the system responsibly and maintain user access controls.

⸻

8. Annual Renewal and Reaffirmation

Each year, or upon election of a new Board, the Association will reaffirm this Agreement electronically.
During reaffirmation, the following statement will appear:

"I reaffirm that the Board of Directors, through its duly authorized officer, continues the Association's use of HOA Manager Pro as its official electronic voting, notice, and records platform."

The reaffirmation process maintains continuity and ensures compliance with corporate governance standards.

⸻

9. Termination

Either party may terminate this Agreement upon written notice. Upon termination, HOA Manager Pro shall provide the Association with access to export its records in accordance with the Terms of Service.

⸻

10. Limitation of Liability

To the fullest extent permitted by law, Provider's aggregate liability shall not exceed the fees paid by the Association in the twelve (12) months preceding the claim.
The Provider shall not be liable for indirect, consequential, or punitive damages arising from the Association's use of the Services.

⸻

11. Dispute Resolution

This Agreement is governed by the laws of the State of Florida. Any dispute shall be resolved in the state or federal courts of Santa Rosa County, Florida.

⸻

12. Acceptance and Audit Trail

By clicking "I Agree" or electronically signing below, the Signatory affirms that they:
	1. Are authorized to bind the Association;
	2. Accept this Agreement on behalf of the Association; and
	3. Consent to electronic recordkeeping and transmission for this Agreement.

All acceptance data (name, title, HOA name, timestamp, IP address, and email) will be retained as part of the official record.`;

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900">Association Authorization Agreement</h2>
        <p className="text-gray-600 mt-2">Please review and accept the agreement to continue</p>
      </div>

      {/* Agreement Text */}
      <div className="bg-white border-2 border-gray-300 rounded-lg p-6 max-h-96 overflow-y-auto">
        <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans">
          {agreementText}
        </pre>
      </div>

      {/* Signatory Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 space-y-4">
        <h3 className="font-semibold text-gray-900">Signatory Information</h3>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={authData.signatory_name}
            onChange={(e) => onChange({ ...authData, signatory_name: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            placeholder="John Smith"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title/Position <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={authData.signatory_title}
            onChange={(e) => onChange({ ...authData, signatory_title: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            placeholder="Board President"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email Address <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            value={authData.signatory_email}
            onChange={(e) => onChange({ ...authData, signatory_email: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            placeholder="president@hoa.com"
          />
        </div>

        <div className="text-xs text-gray-600 bg-white p-3 rounded border border-gray-200">
          <p className="font-medium mb-1">Data Collection Notice:</p>
          <p>The following information will be collected and retained as part of the official record:</p>
          <ul className="list-disc list-inside mt-1 space-y-0.5">
            <li>Name: {authData.signatory_name || '[Not provided]'}</li>
            <li>Title: {authData.signatory_title || '[Not provided]'}</li>
            <li>Email: {authData.signatory_email || '[Not provided]'}</li>
            <li>IP Address: {authData.ip_address || '[Detecting...]'}</li>
            <li>Timestamp: {new Date().toLocaleString()}</li>
          </ul>
        </div>
      </div>

      {/* Agreement Checkboxes */}
      <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-6 space-y-4">
        <h3 className="font-semibold text-gray-900">Required Affirmations</h3>
        
        <label className="flex items-start space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={authData.agreed_authority}
            onChange={(e) => onChange({ ...authData, agreed_authority: e.target.checked })}
            className="mt-1 h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
          />
          <span className="text-sm text-gray-800">
            <strong>I affirm that I have the authority</strong> to act on behalf of this HOA/Association and this agreement is binding.
          </span>
        </label>

        <label className="flex items-start space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={authData.agreed_terms}
            onChange={(e) => onChange({ ...authData, agreed_terms: e.target.checked })}
            className="mt-1 h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
          />
          <span className="text-sm text-gray-800">
            I agree to the{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 underline">
              Terms of Service
            </a>
          </span>
        </label>

        <label className="flex items-start space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={authData.agreed_privacy}
            onChange={(e) => onChange({ ...authData, agreed_privacy: e.target.checked })}
            className="mt-1 h-5 w-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
          />
          <span className="text-sm text-gray-800">
            I agree to the{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 underline">
              Privacy Policy
            </a>
          </span>
        </label>
      </div>

      <div className="bg-gray-50 border border-gray-300 rounded-lg p-4 text-sm text-gray-600">
        <p className="font-medium mb-2">📋 What happens next:</p>
        <ol className="list-decimal list-inside space-y-1">
          <li>Complete your agreement by checking all boxes above</li>
          <li>Proceed to payment</li>
          <li>Your HOA account will be created after successful payment</li>
          <li>You'll receive confirmation and access credentials via email</li>
        </ol>
      </div>
    </div>
  );
};

export default AuthorizationAgreement;
